import { GlassPanel } from '@/components/GlassPanel';
import { KpiStrip } from '@/components/KpiStrip';
import { Warehouse, MapPin, GraduationCap, Users, Clock } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { t } from '@/i18n/translations';

export const LogisticsSection = () => {
  const { lang } = useLanguage();
  const tx = t(lang).logistics;
  return (
    <section
      id="logistics"
      data-testid="logistics-section"
      className="scroll-section min-h-screen flex items-center px-4"
    >
      <div className="section-content max-w-[1200px] mx-auto">
        {/* Section label */}
        <div className="flex items-center gap-3 mb-8">
          <span className="w-8 h-px bg-[#C87533]" />
          <span className="font-mono text-xs text-[#C87533] uppercase tracking-wider">{tx.label}</span>
        </div>

        <div className="grid gap-8 lg:grid-cols-12 items-start">
          {/* Left: Content */}
          <div className="lg:col-span-6 xl:col-span-5">
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight mb-2 text-white">
              {tx.address}
            </h2>
            <p className="text-white/50 text-sm mb-6">{tx.addressSub}</p>

            <div className="space-y-5 mb-8">
              <div className="flex gap-4 items-start">
                <div className="w-10 h-10 rounded-xl bg-[#C87533]/15 flex items-center justify-center flex-shrink-0">
                  <Warehouse className="w-5 h-5 text-[#C87533]" />
                </div>
                <div>
                  <h3 className="font-display text-base font-semibold text-white mb-1">{tx.feat1Title}</h3>
                  <p className="text-sm text-white/65 leading-relaxed">{tx.feat1Body}</p>
                </div>
              </div>

              <div className="flex gap-4 items-start">
                <div className="w-10 h-10 rounded-xl bg-[#C87533]/15 flex items-center justify-center flex-shrink-0">
                  <Clock className="w-5 h-5 text-[#C87533]" />
                </div>
                <div>
                  <h3 className="font-display text-base font-semibold text-white mb-1">{tx.feat2Title}</h3>
                  <p className="text-sm text-white/65 leading-relaxed">{tx.feat2Body}</p>
                </div>
              </div>

              <div className="flex gap-4 items-start">
                <div className="w-10 h-10 rounded-xl bg-[#C87533]/15 flex items-center justify-center flex-shrink-0">
                  <GraduationCap className="w-5 h-5 text-[#C87533]" />
                </div>
                <div>
                  <h3 className="font-display text-base font-semibold text-white mb-1">{tx.feat3Title}</h3>
                  <p className="text-sm text-white/65 leading-relaxed">{tx.feat3Body}</p>
                </div>
              </div>
            </div>

            <KpiStrip
              cols={3}
              kpis={[
                { value: 200, suffix: ' m²', label: tx.kpi1 },
                { value: 40, suffix: '+', label: tx.kpi2 },
                { value: 5, suffix: ' min', label: tx.kpi3 },
              ]}
            />
          </div>

          {/* Right: Warehouse 3D visual */}
          <div className="lg:col-span-6 xl:col-span-7">
            <GlassPanel variant="strong" className="p-6 sm:p-8 relative overflow-hidden">
              {/* Warehouse cross-section visualization */}
              <div className="flex items-center justify-between mb-6">
                <span className="font-mono text-xs text-white/50 uppercase tracking-wider">
                  {tx.diagramLabel}
                </span>
                <MapPin className="w-4 h-4 text-[#C87533]" />
              </div>

              {/* Diagram dveh lokacij */}
              <div className="relative aspect-[4/3] bg-gradient-to-b from-[#0F223B] to-[#07101E] rounded-xl overflow-hidden border border-white/5">

                {/* ── LETALIŠKA 34 (leva stran) ── */}
                {/* Naslov */}
                <div className="absolute top-[8%] left-[5%]">
                  <span className="font-mono text-[10px] text-white/40 tracking-wider">LETALIŠKA 34</span>
                </div>
                {/* Vodoravna strešna črta */}
                <div className="absolute top-[16%] left-[5%] w-[42%] h-px bg-white/15" />
                {/* Spust na kartici */}
                <div className="absolute top-[16%] left-[16%] w-px h-[18%] bg-white/15" />
                <div className="absolute top-[16%] left-[32%] w-px h-[18%] bg-white/15" />
                {/* Kartici */}
                <div className="absolute top-[34%] left-[5%] w-[18%] h-[36%] border border-[#C87533]/40 rounded-lg bg-[#C87533]/8 flex items-center justify-center">
                  <div className="text-center">
                    <Warehouse className="w-5 h-5 text-[#C87533] mx-auto mb-1" />
                    <span className="font-mono text-[9px] text-[#C87533]/80 block">{tx.warehouse}</span>
                  </div>
                </div>
                <div className="absolute top-[34%] left-[26%] w-[18%] h-[36%] border border-white/15 rounded-lg bg-white/[0.03] flex items-center justify-center">
                  <div className="text-center">
                    <Users className="w-5 h-5 text-white/40 mx-auto mb-1" />
                    <span className="font-mono text-[9px] text-white/40 block">{tx.distribution}</span>
                  </div>
                </div>

                {/* ── LOČILNA PIKČASTA ČRTA ── */}
                <div className="absolute top-[8%] bottom-[8%] left-[50%] border-l border-dashed border-white/10" />

                {/* ── GOGALOVA UL. 1 (desna stran) ── */}
                {/* Naslov */}
                <div className="absolute top-[8%] right-[5%] text-right">
                  <span className="font-mono text-[10px] text-white/40 tracking-wider">GOGALOVA UL. 1</span>
                </div>
                {/* Vodoravna strešna črta */}
                <div className="absolute top-[16%] right-[5%] w-[40%] h-px bg-white/15" />
                {/* Spust na kartico */}
                <div className="absolute top-[16%] right-[24%] w-px h-[18%] bg-white/15" />
                {/* Kartica */}
                <div className="absolute top-[34%] right-[8%] w-[28%] h-[36%] border border-white/15 rounded-lg bg-white/[0.03] flex items-center justify-center">
                  <div className="text-center">
                    <GraduationCap className="w-5 h-5 text-white/40 mx-auto mb-1" />
                    <span className="font-mono text-[9px] text-white/40 block">ACADEMY</span>
                  </div>
                </div>

                {/* Floor grid */}
                <div className="absolute bottom-0 left-0 right-0 h-[15%]" style={{
                  background: 'repeating-linear-gradient(90deg, transparent, transparent 49px, rgba(200,117,51,0.06) 49px, rgba(200,117,51,0.06) 50px)',
                }} />
              </div>

            </GlassPanel>
          </div>
        </div>
      </div>
    </section>
  );
};
